import { storage } from '../firebase';
import { ref, uploadBytes, listAll, getDownloadURL, deleteObject } from 'firebase/storage';
import { setAlbumMetadata, getAlbumMetadata as getFirestoreMetadata, isAdmin } from './firestoreService';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateUploadKey } from '../utils/uploadKeyUtils';

// Helper function to determine if a file is a video
const isVideoFile = (filename) => {
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.m4v', '.mkv'];
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return videoExtensions.includes(ext);
};

const sanitizeAlbumName = (name) => {
  const sanitized = name
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Remove consecutive hyphens
    .replace(/-+/g, '-');
  
  // Append 5-digit random number to prevent folder conflicts
  const randomNumber = Math.floor(Math.random() * 90000) + 10000;
  return `${sanitized}-${randomNumber}`;
};

export const createAlbum = async (albumTitle, metadata) => {
  const sanitizedFolderName = sanitizeAlbumName(albumTitle);
  console.log('Creating album:', { originalTitle: albumTitle, sanitizedFolderName });

  // Store the original title in metadata
  const albumMetadata = {
    ...metadata,
    title: albumTitle,  // Keep original title with special characters
    createdAt: new Date().toISOString(),
    // Set default restriction settings for Firestore rules
    isRestricted: false,  // Albums are public by default
    allowedEmails: [],    // Empty array for public albums
    // Set default cover photo to bear placeholder
    coverPhoto: {
      url: '/bear.png',
      thumbnailUrl: '/bear.png',
      name: 'bear.png',
      cropPosition: 50
    },
    // Generate upload key for anonymous uploads
    uploadKey: generateUploadKey()
  };

  // Store metadata in Firestore instead of Storage JSON
  await setAlbumMetadata(sanitizedFolderName, albumMetadata);

  return sanitizedFolderName;
};

export const getAlbums = async (userEmail = null) => {
  try {
    // Get all albums from Firestore
    const albumsRef = collection(db, 'albums');
    const snapshot = await getDocs(albumsRef);

    const albums = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Check if user is an admin
    const userIsAdmin = userEmail ? await isAdmin(userEmail) : false;

    // Filter albums based on user access
    // Public albums (isRestricted: false) are visible to everyone
    // Restricted albums are only visible to users in allowedEmails or admins
    const accessibleAlbums = albums.filter(album => {
      // Show public albums to everyone
      if (!album.isRestricted) {
        return true;
      }

      // Admins can see all albums (including restricted)
      if (userIsAdmin) {
        return true;
      }

      // For restricted albums, check if user is in allowed list
      if (userEmail && album.allowedEmails?.includes(userEmail.toLowerCase())) {
        return true;
      }

      // Hide restricted albums from unauthorized users
      return false;
    });

    return accessibleAlbums;
  } catch (error) {
    console.error('Error fetching albums from Firestore:', error);
    return [];
  }
};

export const getAlbumMetadata = async (albumId) => {
  try {
    const metadata = await getFirestoreMetadata(albumId);

    if (!metadata) {
      throw new Error('Album metadata not found');
    }

    console.log('getAlbumMetadata: Retrieved metadata for', albumId, ':', metadata);
    return metadata;
  } catch (error) {
    console.log('getAlbumMetadata: No metadata found for', albumId, ', error:', error.message);
    throw error; // Re-throw so setAlbumCover can handle it
  }
};

export const getAlbumPhotos = async (albumId) => {
  const albumRef = ref(storage, `albums/${albumId}/`);
  const result = await listAll(albumRef);

  // Get both original photos/videos and check for thumbnails
  const photos = await Promise.all(
    result.items
      .filter(item => item.name !== 'metadata.json' && !item.fullPath.includes('/thumbs/'))
      .map(async (item) => {
        const fullResUrl = await getDownloadURL(item);
        const isVideo = isVideoFile(item.name);

        // Check if thumbnail exists (for videos, thumbnail will be .jpg)
        let thumbnailUrl = null;
        try {
          // For videos, look for {filename}.jpg in thumbs folder
          const thumbName = isVideo ? item.name + '.jpg' : item.name;
          const thumbRef = ref(storage, `albums/${albumId}/thumbs/${thumbName}`);
          thumbnailUrl = await getDownloadURL(thumbRef);
        } catch (error) {
          // Thumbnail doesn't exist yet, use full-res as fallback (images only)
          console.log('No thumbnail found for:', item.name);
        }

        return {
          id: item.name,
          url: fullResUrl,           // Full resolution for download/modal
          thumbnailUrl: thumbnailUrl || (!isVideo ? fullResUrl : null), // For images, fallback to full-res; for videos, use null until thumbnail is generated
          name: item.name,
          isVideo: isVideo            // Flag to identify videos
        };
      })
  );

  return photos;
};

export const uploadPhoto = async (albumId, file) => {
  const photoRef = ref(storage, `albums/${albumId}/${file.name}`);
  await uploadBytes(photoRef, file);
  const url = await getDownloadURL(photoRef);
  return {
    id: file.name,
    url,
    name: file.name
  };
};

export const deletePhoto = async (albumId, photoName) => {
  // Delete the main file (photo or video)
  const photoRef = ref(storage, `albums/${albumId}/${photoName}`);
  await deleteObject(photoRef);

  // Delete the corresponding thumbnail
  try {
    const isVideo = isVideoFile(photoName);
    // For videos, thumbnail is {filename}.jpg; for images, it's the same filename
    const thumbName = isVideo ? photoName + '.jpg' : photoName;
    const thumbRef = ref(storage, `albums/${albumId}/thumbs/${thumbName}`);
    await deleteObject(thumbRef);
    console.log('Deleted thumbnail:', thumbName);
  } catch (error) {
    // Thumbnail might not exist yet, which is fine
    console.log('No thumbnail to delete for:', photoName);
  }
};

export const updateAlbumMetadata = async (albumId, metadata) => {
  console.log('updateAlbumMetadata: Updating metadata for', albumId, 'with:', metadata);
  await setAlbumMetadata(albumId, metadata);
  console.log('updateAlbumMetadata: Successfully updated metadata in Firestore');
  return metadata;
};

export const setAlbumCover = async (albumId, photoUrl, photoName, thumbnailUrl = null) => {
  try {
    console.log('Setting album cover:', { albumId, photoUrl, photoName, thumbnailUrl });

    let currentMetadata;
    try {
      currentMetadata = await getAlbumMetadata(albumId);
    } catch (error) {
      console.log('No existing metadata found, creating new metadata');
      // Create basic metadata if it doesn't exist
      currentMetadata = {
        id: albumId,
        title: albumId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        date: new Date().toISOString(),
        category: '',
        createdAt: new Date().toISOString()
      };
    }

    console.log('Current metadata:', currentMetadata);

    const updatedMetadata = {
      ...currentMetadata,
      coverPhoto: {
        url: photoUrl,
        thumbnailUrl: thumbnailUrl || photoUrl, // Use thumbnail if available, otherwise full-res
        name: photoName,
        cropPosition: currentMetadata.coverPhoto?.cropPosition || 50
      }
    };
    console.log('Updated metadata:', updatedMetadata);

    await updateAlbumMetadata(albumId, updatedMetadata);
    console.log('Metadata updated successfully');

    return updatedMetadata;
  } catch (error) {
    console.error('Error setting album cover:', error);
    throw error;
  }
};

export const setCoverCropPosition = async (albumId, cropPosition) => {
  try {
    console.log('Setting cover crop position:', { albumId, cropPosition });
    
    const currentMetadata = await getAlbumMetadata(albumId);
    
    if (!currentMetadata.coverPhoto) {
      throw new Error('No cover photo set for this album');
    }
    
    const updatedMetadata = {
      ...currentMetadata,
      coverPhoto: {
        ...currentMetadata.coverPhoto,
        cropPosition: cropPosition
      }
    };
    
    await updateAlbumMetadata(albumId, updatedMetadata);
    console.log('Crop position updated successfully');
    
    return updatedMetadata;
  } catch (error) {
    console.error('Error setting crop position:', error);
    throw error;
  }
};

export const updateAlbumDetails = async (albumId, updatedData) => {
  try {
    console.log('Updating album details:', { albumId, updatedData });
    
    const updatedMetadata = await updateAlbumMetadata(albumId, updatedData);
    console.log('Album details updated successfully');
    
    return updatedMetadata;
  } catch (error) {
    console.error('Error updating album details:', error);
    throw error;
  }
};

export const getExistingCategories = async () => {
  try {
    const albums = await getAlbums();
    const categories = albums
      .map(album => album.category)
      .filter(category => category && category.trim() !== '')
      .filter((category, index, array) => array.indexOf(category) === index) // Remove duplicates
      .sort();
    
    return categories;
  } catch (error) {
    console.error('Error getting existing categories:', error);
    return [];
  }
};

export const deleteAlbum = async (albumId) => {
  // Delete all files in Storage
  const albumRef = ref(storage, `albums/${albumId}/`);
  const result = await listAll(albumRef);

  await Promise.all(
    result.items.map(item => deleteObject(item))
  );

  // Delete metadata from Firestore
  const albumDoc = doc(db, 'albums', albumId);
  await deleteDoc(albumDoc);

  console.log('Album deleted from both Storage and Firestore:', albumId);
};